import React from "react";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Link,
  Image,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaLinkedin } from "react-icons/fa";
import { HiLocationMarker, HiMail } from "react-icons/hi";
import { iconElement } from "../utils/icons";
import { useNavigate } from "react-router-dom";

export const GreshamFooter: React.FC = () => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue("#f9fafa", "gray.900");
  const textColor = useColorModeValue("gray.800", "white");
  const linkColor = useColorModeValue("gray.600", "gray.400");

  const handleNavigation = (href: string) => {
    navigate(href);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box
      as="footer"
      bg={bgColor}
      py={16}
      className="has_ae_slider elementor-element elementor-element-fbce596 e-con-full e-flex ae-bg-gallery-type-default e-con e-parent e-lazyloaded"
      position="relative"
    >
      {/* Desktop Layout - Side by Side */}
      <Flex 
        direction={{ base: "column", md: "row" }} 
        gap={{ base: 6, md: 8 }}
        align="flex-start"
        display={{ base: "none", sm: "flex" }}
        w="full"
        px={{ base: 6, md: 8, lg: 12 }}
      >
        {/* Left Container - Logo and Description */}
        <Box
          className="has_ae_slider elementor-element elementor-element-99e2ecf e-con-full e-flex ae-bg-gallery-type-default e-con e-child"
          maxW="600px"
          flex="1"
          pl={6}
        >
            {/* Logo - Positioned to align with header */}
            <Box 
              className="elementor-element elementor-element-cd95715 elementor-widget elementor-widget-theme-site-logo elementor-widget-image"
              mb={6}
            >
              <Box className="elementor-widget-container">
                <Image 
                  src="/logo/logo_black.png"
                  alt="Mercurian Equity"
                  height={{ base: "40px", md: "48px" }}
                  width="auto"
                  maxW="400px"
                  objectFit="contain"
                />
              </Box>
            </Box>

            <VStack align="flex-start" spacing={6} w="full">
              {/* Company Description */}
              <Box>
                <Text
                  fontSize="sm"
                  color={textColor}
                  lineHeight="1.6"
                  opacity={0.9}
                  maxW="300px"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Mercurian Equity is an investment management firm, focused on delivering investment solutions and strategic capital partnerships for global institutions and wholesale clients.
                </Text>
              </Box>

              {/* Social Icons */}
              <Box className="elementor-element elementor-element-6f09b47 elementor-shape-square e-grid-align-left e-grid-align-mobile-center elementor-grid-0 elementor-widget elementor-widget-social-icons">
                <Box className="elementor-widget-container">
                  <HStack className="elementor-social-icons-wrapper elementor-grid" spacing={3}>
                    <Link
                      href="https://www.linkedin.com/company/mercurianequity/?viewAsMember=true"
                      target="_blank"
                      className="elementor-icon elementor-social-icon elementor-social-icon-linkedin"
                      _hover={{ opacity: 0.7 }}
                    >
                      <Box
                        w={5}
                        h={5}
                        color={linkColor}
                        borderRadius="sm"
                        p={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {iconElement(FaLinkedin, { width: "20px", height: "20px", color: linkColor })}
                      </Box>
                    </Link>
                  </HStack>
                </Box>
              </Box>

              {/* Copyright Section */}
              <Box>
                <Text
                  fontSize="sm"
                  color={textColor}
                  opacity={0.9}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Copyright © 2025 Mercurian Equity Pty Ltd
                </Text>
              </Box>
            </VStack>
          </Box>

          {/* Right Container - Three Columns */}
          <Box flex="1">
            <Flex
              direction="row"
              gap={8}
              align="flex-start"
            >
            {/* Column 1 - Quick Links Container */}
            <Box
              flex="1"
              minW={{ md: "120px", lg: "140px" }}
              pr={{ base: 4, md: 6, lg: 8 }}
            >
              <VStack align="flex-start" spacing={3}>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Bold', sans-serif"
                  fontWeight="bold"
                  letterSpacing="1.5px"
                  mb={3}
                >
                  Quick Links
                </Text>
                <Link
                  onClick={() => handleNavigation("/")}
                  fontSize="sm"
                  color={textColor}
                  opacity={0.9}
                  _hover={{ color: textColor, opacity: 1 }}
                  cursor="pointer"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Home
                </Link>
                <Link
                  onClick={() => handleNavigation("/about")}
                  fontSize="sm"
                  color={textColor}
                  opacity={0.9}
                  _hover={{ color: textColor, opacity: 1 }}
                  cursor="pointer"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  About
                </Link>
                <Link
                  onClick={() => handleNavigation("/fund")}
                  fontSize="sm"
                  color={textColor}
                  opacity={0.9}
                  _hover={{ color: textColor, opacity: 1 }}
                  cursor="pointer"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Fund
                </Link>
                <Link
                  onClick={() => handleNavigation("/advisory")}
                  fontSize="sm"
                  color={textColor}
                  opacity={0.9}
                  _hover={{ color: textColor, opacity: 1 }}
                  cursor="pointer"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Advisory
                </Link>
                <Link
                  onClick={() => handleNavigation("/contact")}
                  fontSize="sm"
                  color={textColor}
                  opacity={0.9}
                  _hover={{ color: textColor, opacity: 1 }}
                  cursor="pointer"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Contact
                </Link>
              </VStack>
            </Box>

            {/* Column 2 - Legal Container */}
            <Box
              flex="0.8"
              minW={{ md: "100px", lg: "120px" }}
              pr={{ base: 4, md: 6, lg: 8 }}
            >
              <VStack align="flex-start" spacing={3}>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Bold', sans-serif"
                  fontWeight="bold"
                  letterSpacing="1.5px"
                  mb={3}
                >
                  Legal
                </Text>
                <Link
                  onClick={() => handleNavigation("/privacy-policy")}
                  fontSize="sm"
                  color={textColor}
                  opacity={0.9}
                  _hover={{ color: textColor, opacity: 1 }}
                  cursor="pointer"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Privacy Policy
                </Link>
                <Link
                  onClick={() => handleNavigation("/disclaimer")}
                  fontSize="sm"
                  color={textColor}
                  opacity={0.9}
                  _hover={{ color: textColor, opacity: 1 }}
                  cursor="pointer"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Disclaimer
                </Link>
              </VStack>
            </Box>
            
            {/* Column 3 - Get in touch Container */}
            <Box
              flex="1.5"
              minW={{ md: "180px", lg: "220px" }}
            >
              <VStack align="flex-start" spacing={3}>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Bold', sans-serif"
                  fontWeight="bold"
                  letterSpacing="1.5px"
                  mb={3}
                >
                  Get in touch
                </Text>
                <Text
                  fontSize="sm"
                  color={textColor}
                  lineHeight="1.6"
                  opacity={0.9}
                  mb={1}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Mercurian Equity Pty Ltd ACN 686 602 022 CAR No. 1316987
                </Text>
                <HStack spacing={2} align="flex-start">
                  <Box
                    bg={linkColor}
                    borderRadius="full"
                    p={1}
                    minW="20px"
                    h="20px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mt="1px"
                  >
                    {iconElement(HiLocationMarker, { width: "12px", height: "12px", color: "white" })}
                  </Box>
                  <Text
                    fontSize="sm"
                    color={textColor}
                    lineHeight="1.6"
                    opacity={0.9}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    fontWeight="300"
                  >
                    Level 28, 140 St Georges Terrace, Perth, Western Australia, 6000
                  </Text>
                </HStack>
                
                <HStack spacing={2} align="center">
                  <Box
                    bg={linkColor}
                    borderRadius="full"
                    p={1}
                    minW="20px"
                    h="20px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {iconElement(HiMail, { width: "12px", height: "12px", color: "white" })}
                  </Box>
                  <Text
                    fontSize="sm"
                    color={textColor}
                    opacity={0.9}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    fontWeight="300"
                  >
                    info@mercurianequity.com
                  </Text>
                </HStack>
              </VStack>
            </Box>
            </Flex>
          </Box>
        </Flex>

      {/* Mobile Version - All Columns */}
      <Box display={{ base: "block", sm: "none" }} mt={0} px={{ base: 6, md: 8, lg: 12 }}>
        <Flex direction="column" gap={6}>
            {/* Mobile Left Container - Logo and Description */}
            <Box
              w="full"
              mb={6}
              pl={4}
            >
              <VStack align="flex-start" spacing={6} w="full">
                {/* Logo */}
                <Box className="elementor-element elementor-element-cd95715 elementor-widget elementor-widget-theme-site-logo elementor-widget-image">
                  <Box className="elementor-widget-container">
                    <Image 
                      src="/logo/logo_black.png"
                      alt="Mercurian Equity"
                      height={{ base: "40px", md: "48px" }}
                      width="auto"
                      maxW="400px"
                      objectFit="contain"
                    />
                  </Box>
                </Box>

                {/* Company Description */}
                <Box>
                  <Text
                    fontSize="sm"
                    color={linkColor}
                    lineHeight="1.6"
                    opacity={0.8}
                    maxW="300px"
                  >
                    Mercurian Equity is an investment management firm, focused on delivering investment solutions and strategic capital partnerships for global institutions and wholesale clients.
                  </Text>
                </Box>

                {/* Social Icons */}
                <Box className="elementor-element elementor-element-6f09b47 elementor-shape-square e-grid-align-left e-grid-align-mobile-center elementor-grid-0 elementor-widget elementor-widget-social-icons">
                  <Box className="elementor-widget-container">
                    <HStack className="elementor-social-icons-wrapper elementor-grid" spacing={3}>
                      <Link
                        href="https://www.linkedin.com/company/mercurianequity/?viewAsMember=true"
                        target="_blank"
                        className="elementor-icon elementor-social-icon elementor-social-icon-linkedin"
                        _hover={{ opacity: 0.7 }}
                      >
                        <Box
                          w={5}
                          h={5}
                          color={linkColor}
                          borderRadius="sm"
                          p={1}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {iconElement(FaLinkedin, { width: "20px", height: "20px", color: linkColor })}
                        </Box>
                      </Link>
                    </HStack>
                  </Box>
                </Box>

                {/* Copyright Section */}
                <Box>
                  <Text
                    fontSize="sm"
                    color={linkColor}
                    opacity={0.8}
                  >
                    Copyright © 2025 Mercurian Equity Pty Ltd
                  </Text>
                </Box>
              </VStack>
            </Box>

            {/* Column 1 - Quick Links Container */}
            <Box
              w="full"
              mb={6}
            >
              <VStack align="flex-start" spacing={3}>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Bold', sans-serif"
                  fontWeight="bold"
                  letterSpacing="1.5px"
                  mb={3}
                >
                  Quick Links
                </Text>
                <Link
                  onClick={() => handleNavigation("/")}
                  fontSize="sm"
                  color={textColor}
                  opacity={0.9}
                  _hover={{ color: textColor, opacity: 1 }}
                  cursor="pointer"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Home
                </Link>
                <Link
                  onClick={() => handleNavigation("/about")}
                  fontSize="sm"
                  color={textColor}
                  opacity={0.9}
                  _hover={{ color: textColor, opacity: 1 }}
                  cursor="pointer"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  About
                </Link>
                <Link
                  onClick={() => handleNavigation("/fund")}
                  fontSize="sm"
                  color={textColor}
                  opacity={0.9}
                  _hover={{ color: textColor, opacity: 1 }}
                  cursor="pointer"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Fund
                </Link>
                <Link
                  onClick={() => handleNavigation("/advisory")}
                  fontSize="sm"
                  color={textColor}
                  opacity={0.9}
                  _hover={{ color: textColor, opacity: 1 }}
                  cursor="pointer"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Advisory
                </Link>
                <Link
                  onClick={() => handleNavigation("/contact")}
                  fontSize="sm"
                  color={textColor}
                  opacity={0.9}
                  _hover={{ color: textColor, opacity: 1 }}
                  cursor="pointer"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Contact
                </Link>
              </VStack>
            </Box>

            {/* Column 2 - Legal Container */}
            <Box
              w="full"
              mb={6}
            >
              <VStack align="flex-start" spacing={3}>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Bold', sans-serif"
                  fontWeight="bold"
                  letterSpacing="1.5px"
                  mb={3}
                >
                  Legal
                </Text>
                <Link
                  onClick={() => handleNavigation("/privacy-policy")}
                  fontSize="sm"
                  color={textColor}
                  opacity={0.9}
                  _hover={{ color: textColor, opacity: 1 }}
                  cursor="pointer"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Privacy Policy
                </Link>
              </VStack>
            </Box>
            
            {/* Column 3 - Get in touch Container */}
            <Box
              w="full"
              mb={6}
            >
              <VStack align="flex-start" spacing={3}>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Bold', sans-serif"
                  fontWeight="bold"
                  letterSpacing="1.5px"
                  mb={3}
                >
                  Get in touch
                </Text>
                <Text
                  fontSize="sm"
                  color={textColor}
                  lineHeight="1.6"
                  opacity={0.9}
                  mb={1}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  fontWeight="300"
                >
                  Mercurian Equity Pty Ltd ACN 686 602 022 CAR No. 1316987
                </Text>
                <HStack spacing={2} align="flex-start">
                  <Box
                    bg={linkColor}
                    borderRadius="full"
                    p={1}
                    minW="20px"
                    h="20px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mt="1px"
                  >
                    {iconElement(HiLocationMarker, { width: "12px", height: "12px", color: "white" })}
                  </Box>
                  <Text
                    fontSize="sm"
                    color={textColor}
                    lineHeight="1.6"
                    opacity={0.9}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    fontWeight="300"
                  >
                    Level 28, 140 St Georges Terrace, Perth, Western Australia, 6000
                  </Text>
                </HStack>
                
                <HStack spacing={2} align="center">
                  <Box
                    bg={linkColor}
                    borderRadius="full"
                    p={1}
                    minW="20px"
                    h="20px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {iconElement(HiMail, { width: "12px", height: "12px", color: "white" })}
                  </Box>
                  <Text
                    fontSize="sm"
                    color={textColor}
                    opacity={0.9}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    fontWeight="300"
                  >
                    info@mercurianequity.com
                  </Text>
                </HStack>
              </VStack>
            </Box>
        </Flex>
      </Box>
    </Box>
  );
};